import { useEffect, useMemo, useRef } from 'react'
import { Tabs } from '@pcln/horizon'
import type { ProductType } from '@/types'
import { PRODUCT_TABS_EXPERIMENT, tabIconUrls } from '../SearchForm/constants'
import getProductCategory from './utils/getProductCategory'

function generateProductTabs(tabs: typeof PRODUCT_TABS_EXPERIMENT) {
  return tabs.map(productTab => {
    const { id, tabText } = productTab
    return {
      ...productTab,
      iconLeft: ({ className }: { className?: string }) => (
        // eslint-disable-next-line @next/next/no-img-element
        <img alt={id} src={tabIconUrls[id]} className={className} />
      ),
      panelContent: getProductCategory(productTab.id).component,
      value: id,
      label: tabText
    } as const
  })
}

export default function SearchFormExperimentWrapper({
  currentTab,
  onFormChange
}: {
  currentTab: ProductType
  onFormChange: (productType: ProductType) => void
}) {
  const prevTabRef = useRef<ProductType>(currentTab)

  // Sync prevTabRef when currentTab changes externally (e.g., browser back button)
  useEffect(() => {
    prevTabRef.current = currentTab
  }, [currentTab])

  const handleOnTabClick = (productType: ProductType) => {
    // it often happens that baseUI tabs are responding to both a "mousedown" and "click" event for one user action
    // seems to be a weird edge case due to our rendering architecture and some base UI internal behavior
    // this check prevents onFormChange() from being invoked twice for the same tab click
    if (prevTabRef.current === productType) {
      return
    }

    prevTabRef.current = productType
    onFormChange(productType)
  }

  const tabsContent = useMemo(
    () => generateProductTabs(PRODUCT_TABS_EXPERIMENT),
    []
  )

  return (
    <div className="w-auto">
      <div className="mx-auto p-4 max-w-7xl flex flex-col justify-center gap-4">
        <div className="w-full pt-3 px-3 md:pt-0 md:px-0 flex justify-center">
          <Tabs
            stacked
            tabsContent={tabsContent}
            // value means tabs are "controlled" and respond to currentTab changes, not its own internal state (defaultValue for that)
            value={currentTab}
            onValueChange={handleOnTabClick}
            slotClassNames={{
              rootClass: 'overflow-visible', // box shadow overflow, cut off by parent
              listClass:
                'flex gap-1 md:gap-3 justify-between min-md:justify-center',
              panelClass:
                'w-full max-w-7xl mt-4 bg-primary-1 md:p-4 md:rounded-xl md:shadow-lg'
            }}
          />
        </div>
      </div>
    </div>
  )
}
